import { BoxProps } from '@chakra-ui/react';
import {
  FunctionComponent as FC,
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';
import { Link as ReactDomLink } from 'react-router-dom';
import {
  doPreviewInvoice,
  usePreviewInvoiceJson,
} from '../../../api/accountsService';
import { InvoiceTaxCalculationError } from '../../../components/Invoices/InvoiceTaxCalculationError';
import {
  MAlert,
  MBox,
  MButton,
  MCustomIconButton,
  MLink,
} from '../../../components/Monetize';
import PreviewPdfDrawer from '../../../components/Monetize/PreviewPdfDrawer/PreviewPdfDrawer';
import { TAX_ERROR_CAN_RESOLVE_STATUSES } from '../../../constants/invoice';
import { getBillingScheduleRoute } from '../../../constants/routes';

interface PreviewBillGroupInvoicePdfDrawerProps extends BoxProps {
  accountId: string;
  billGroupId: string;
}

/**
 * Allow user to click a button to open a drawer and view invoice PDF document for a bill group
 */
export const PreviewBillGroupInvoicePdfDrawer: FC<
  PreviewBillGroupInvoicePdfDrawerProps
> = ({
  accountId,
  billGroupId,
  ...rest
}: PreviewBillGroupInvoicePdfDrawerProps) => {
  const buttonRef = useRef<typeof MCustomIconButton>(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <MBox position="relative" {...rest}>
      <MButton variant="tertiary" onClick={() => setIsOpen(true)}>
        Preview Invoice
      </MButton>
      {isOpen && (
        <BillGroupInvoicePreviewDrawer
          accountId={accountId}
          billGroupId={billGroupId}
          onCloseFocusRef={buttonRef}
          onClose={() => setIsOpen(false)}
        />
      )}
    </MBox>
  );
};

interface BillGroupInvoicePreviewDrawerProps {
  accountId: string;
  billGroupId: string;
  onClose: () => void;
  onCloseFocusRef?: RefObject<any>;
}

/** The invoice preview drawer on its own, for callers that open it from their own trigger */
export const BillGroupInvoicePreviewDrawer = ({
  accountId,
  billGroupId,
  onClose,
  onCloseFocusRef,
}: BillGroupInvoicePreviewDrawerProps) => {
  const { data: invoice } = usePreviewInvoiceJson({ accountId, billGroupId });

  const fetchDocument = useCallback(
    async (): Promise<ArrayBuffer | null> =>
      await doPreviewInvoice(billGroupId),
    [billGroupId],
  );

  return (
    <PreviewPdfDrawer
      filename="invoice-preview"
      isOpen
      onCloseFocusRef={onCloseFocusRef}
      fetchDocument={fetchDocument}
      onClose={onClose}
      fallbackContent={<FallBackContent billGroupId={billGroupId} />}
    >
      <InvoiceTaxCalculationError
        taxError={invoice?.taxError}
        showReviewAction={
          invoice?.status && TAX_ERROR_CAN_RESOLVE_STATUSES.has(invoice.status)
        }
      />
    </PreviewPdfDrawer>
  );
};

const FallBackContent = ({ billGroupId }: { billGroupId: string }) => {
  const billGroupRoute = getBillingScheduleRoute(billGroupId);
  return (
    <MAlert
      alertIconProps={{ boxSize: 4 }}
      alertDescriptionProps={{ fontSize: 'md' }}
      alignSelf="stretch"
      type="info"
    >
      Based on the{' '}
      <MLink asChild>
        <ReactDomLink to={billGroupRoute}>billing schedule</ReactDomLink>
      </MLink>{' '}
      , there are no additional invoices scheduled for the current contract.
    </MAlert>
  );
};
